(ns practitest.frontend.components2.dashboard.charts.axis
  (:require ["d3" :as d3]
            [clojure.string :as str]
            [practitest.frontend.components2.dashboard.charts.common :as c]
            [practitest.frontend.components2.dashboard.charts.render-helpers :as rh]
            [practitest.frontend.common.format-utils :as format]
            [tick.core :as t]))

(defn- rotate-ticks! [sel {:keys [label-angle]}]
  (when label-angle
    (-> (.selectAll sel ".tick text")
        (.attr "transform" (str "rotate(" label-angle ")"))
        (.attr "dx" (if (neg? label-angle) "-0.5em" "0.5em"))
        (.attr "dy" "0.5em")
        (.style "text-anchor" (if (neg? label-angle) "end" "start")))))

(defn- angle-cos [angle-deg]
  (js/Math.cos (* angle-deg (/ js/Math.PI 180))))

(defn- center-rotated-labels!
  "Shifts each rotated label so its horizontal extent is centered on its tick.
   A label wider than the band is right-aligned to the band's right edge
   instead, so it extends to the left."
  [sel {:keys [label-angle banded?]} d3-scale]
  (when (and label-angle banded?)
    (let [rad       (* label-angle (/ js/Math.PI 180))
          cos       (js/Math.cos rad)
          sin       (js/Math.sin rad)
          half-step (/ (.step d3-scale) 2)]
      (let [texts  (.selectAll sel ".tick text")
            shifts (mapv (fn [^js el]
                           (let [bbox  (.getBBox el)
                                 x0    (.-x bbox)
                                 x1    (+ x0 (.-width bbox))
                                 y0    (.-y bbox)
                                 y1    (+ y0 (.-height bbox))
                                 xs    (for [x [x0 x1] y [y0 y1]] (- (* x cos) (* y sin)))
                                 min-x (apply min xs)
                                 max-x (apply max xs)]
                             (if (<= (- max-x min-x) (* 2 half-step))
                               (- (/ (+ min-x max-x) 2))
                               (- half-step max-x))))
                         (.nodes texts))]
        (.attr texts "transform"
               (fn [_ i] (str "translate(" (nth shifts i) ",0) rotate(" label-angle ")")))))))

(defn- rotated-max-label-width
  [angle-deg bottom-padding]
  (/ bottom-padding (js/Math.sin (* (js/Math.abs angle-deg) (/ js/Math.PI 180)))))

(defn- discrete-scale?
  [d3-scale]
  (boolean (and d3-scale (fn? (.-step d3-scale)))))

(defn- max-label-width
  [{:keys [axis type d3-scale]} bottom-padding]
  (when (and (not= :temporal type) (discrete-scale? d3-scale))
    (if-let [angle (:label-angle axis)]
      (cond-> (rotated-max-label-width angle bottom-padding)
        (:banded? axis) (min (/ (+ (.step d3-scale) 15)
                                (angle-cos angle))))
      (.step d3-scale))))

(defn- tick-anchor-x
  [d3-scale d]
  (let [x (d3-scale d)]
    (if (fn? (.-bandwidth d3-scale))
      (+ x (/ (.bandwidth d3-scale) 2))
      x)))

(defn- wire-label-tooltip!
  "Shows the chart's tooltip with the label's full text on hover, anchored
   above the label, pointing down at it."
  [^js text-el full {:keys [d3-scale d height tooltip]}]
  (let [{:keys [show! hide!]} tooltip]
    (-> (.select d3 text-el)
        (.on "mouseenter" (fn [] (show! {:category  full
                                         :rows      []
                                         :placement :above
                                         :anchor-x  (tick-anchor-x d3-scale d)
                                         :anchor-y  height})))
        (.on "mouseleave" (fn [] (hide!))))))

(defn- truncate-labels!
  [sel {:keys [d3-scale] :as channel} {:keys [bottom-padding height tooltip]}]
  (when-let [max-w (max-label-width channel bottom-padding)]
    (-> sel
        (.selectAll ".tick text")
        (.each (fn [^js d]
                 (this-as this
                   (let [full (.-textContent this)]
                     (when (and (rh/ellipsize-label! this full max-w) tooltip)
                       (wire-label-tooltip!
                        this full {:d3-scale d3-scale :d d :height height :tooltip tooltip})))))))))

(defn- show-every-nth-label! [sel n]
  (-> (.selectAll sel ".tick text")
      (.style "display" (fn [_ i]
                          (if (zero? (mod i n)) "block" "none")))))

(defn- d3-format [type fmt]
  (case type
    :temporal     (.utcFormat d3 fmt)
    :quantitative (.format d3 fmt)
    nil))

(defn- exact-tick-values [scale n]
  (let [[d0 d1] (.domain scale)
        t0      (.valueOf d0)
        t1      (.valueOf d1)
        step    (/ (- t1 t0) (dec n))]
    (clj->js (mapv #(+ t0 (* % step)) (range n)))))

(defn- cross-len [orient width height]
  (case orient
    (:top :bottom) height
    (:left :right) width))

(defn- orient->d3-axis [orient d3-scale]
  (case orient
    :top    (.axisTop d3 d3-scale)
    :bottom (.axisBottom d3 d3-scale)
    :left   (.axisLeft d3 d3-scale)
    :right  (.axisRight d3 d3-scale)))

(defn- min-step-tick-values [scale tick-min-step]
  (let [[d0 d1] (.domain scale)
        start   (* tick-min-step (js/Math.ceil (/ d0 tick-min-step)))
        end     (* tick-min-step (js/Math.floor (/ d1 tick-min-step)))
        n-steps (if (<= start end) (/ (- end start) tick-min-step) -1)]
    (if (< n-steps 8)
      (clj->js (range start (+ end tick-min-step) tick-min-step))
      (->> (.ticks scale 8)
           js->clj
           (map #(-> (/ % tick-min-step)
                     js/Math.round
                     (* tick-min-step)))
           distinct
           sort
           clj->js))))

(defn tick-step
  [scale tick-min-step]
  (let [values (js->clj (if (and tick-min-step (pos? tick-min-step))
                          (min-step-tick-values scale tick-min-step)
                          (.ticks scale)))
        [a b]  (take-last 2 values)]
    (when (and a b) (- b a))))

(defn- axis-format [type format d3-scale valid-step?]
  (or format
      (when (= type :quantitative)
        (let [[d0 d1]   (.domain d3-scale)
              max-value (max (js/Math.abs d0)
                              (js/Math.abs d1))]
          (cond
            (>= max-value 1000) ".3~s"
            valid-step?        ".0f")))))

(defn- d3-axis [{:keys [type d3-scale axis labels]} width height]
  (let [{:keys [orient format tick-count values ticks grid tick-min-step]} axis
        tick-size      (- (cross-len orient width height))
        q?             (= type :quantitative)
        valid-step?    (and (number? tick-min-step)
                            (pos? tick-min-step))
        fmt            (axis-format type format d3-scale valid-step?)]
    (cond-> (orient->d3-axis orient d3-scale)
      fmt                            (.tickFormat (d3-format type fmt))
      labels                         (.tickFormat #(get labels % %))
      (and q? valid-step?
           (not (false? ticks)))     (.tickValues (min-step-tick-values d3-scale tick-min-step))
      values                    (.tickValues (clj->js values))
      (and (not valid-step?) (not values) tick-count)
                                     (.tickValues (exact-tick-values d3-scale tick-count))
      grid                           (-> (.tickSize tick-size) (.tickSizeOuter 0))
      (false? ticks)                 (.tickValues #js []))))

(defn- title-transform [orient width height]
  (case orient
    :top    (str "translate(" (/ width 2) ",-25)")
    :bottom (str "translate(" (/ width 2) ",35)")
    :left   (str "translate(-40," (/ height 2) ") rotate(-90)")
    :right  (str "translate(40," (/ height 2) ") rotate(90)")))

(defn- ensure-title! [sel {:keys [orient title]} width height]
  (when title
    (-> (c/ensure-el! sel "text" "axis-title")
        (.attr "text-anchor" "middle")
        (.attr "transform" (title-transform orient width height))
        (.text title))))

(defn- band-separators!
  "Draws the axis's baseline as one line across the bands, with a tick half a
   gap before each band instead of the ticks at band centers."
  [sel d3-scale width]
  (let [bw    (.bandwidth d3-scale)
        gap   (- (.step d3-scale) bw)
        xs    (map d3-scale (js->clj (.domain d3-scale)))
        line  (str "M0,0L" width ",0")
        ticks (->> xs
                   (map (fn [x0]
                          (let [x (- x0 (/ gap 2))]
                            (str "M" x ",0L" x ",6"))))
                   (str/join ""))]
    (-> sel (.selectAll ".tick line") (.style "display" "none"))
    (-> sel (.select "path.domain") (.attr "d" (str line ticks)))))

(defn- render-axis!
  [sel {:keys [axis d3-scale] :as channel} {:keys [width height bottom-padding tooltip]}]
  (when channel
    (let [d3-ax     (d3-axis channel width height)
          sel       (-> (.call sel d3-ax)
                        (.classed "with-grid" (boolean (:grid axis))))
          label-stride (:label-stride axis)]
      (when label-stride
        (show-every-nth-label! sel label-stride))
      (truncate-labels!
       sel channel {:bottom-padding bottom-padding :height height :tooltip tooltip})
      (rotate-ticks! sel axis)
      (center-rotated-labels! sel axis d3-scale)
      (ensure-title! sel axis width height)
      (when (and (:banded? axis) (discrete-scale? d3-scale))
        (band-separators! sel d3-scale width)))))

(defn render!
  "Renders the x and y axes into svg. y-svg, when given, receives the y-axis
   instead — the split layout keeps it outside the scrollable plot."
  ([svg ctx] (render! svg svg ctx))
  ([svg y-svg {{:keys [x y]} :encode
               :keys         [width height padding tooltip]}]
   (let [x-g   (.select svg "g.chart g.x-axis")
         y-g   (.select y-svg "g.chart g.y-axis")
         opts  {:width width
                :height height
                :bottom-padding (:bottom padding)
                :tooltip tooltip}]
     (render-axis! x-g x opts)
     (render-axis! y-g y opts))))

